import React, { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import baseService from "services/BaseService";
import Loader from "components/Loader";
import DynamicForm from "../../../pages/Ordering/common/DynamicForm";
import QRCodeChannels from "./QRCodeChannels";

const QR_CODE_CHANNELS = ["SLACK_CHANNEL", "TELEGRAM_CHANNEL"];

const ACTIVATION_CHANNELS = ["POPUP_CHANNEL"];
const EMAIL_CHANNELS = ["EMAIL_CHANNEL"];

const NotificationChannels = props => {
    const { t, canEditNotificationChannels } = props;

    const [notificationChannelsData, setNotificationChannelsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notificationChannelsTS, setNotificationChannelsTS] = useState(null);

    const activationChannelsDynData = useMemo(() => {
        if (!notificationChannelsData || !Array.isArray(notificationChannelsData)) {
            return [];
        }
        const filteredChannels = notificationChannelsData.filter(channel => ACTIVATION_CHANNELS.includes(channel.code));
        const blockTitle = get(filteredChannels[0], "name", "");
        const channels = filteredChannels.flatMap(channel => channel.channels);
        const widgets = channels.map(channel => {
            const status = channel && channel.enabled ? "active" : "inactive";
            return {
                disabled: !canEditNotificationChannels,
                title: blockTitle,
                key: `activateChannel_${channel.id}`, // redux form could not in numbers or even string numbers, so we need that hack
                savedValue: status,
                widgetType: "select",
                values: [
                    { value: `${t("notificationChannels.activatePushNotification")} ${channel.value}`, key: "active" },
                    { value: `${t("notificationChannels.disablePushNotification")} ${channel.value}`, key: "inactive" },
                ],
                type: channel.code,
                customLiveSubmit: data => {
                    const { attrValue, attrCode } = data.data;
                    const [_, id] = attrCode.split("_");
                    const newData = {
                        pathParams: { id },
                        data: {
                            status: attrValue,
                        },
                        jsonType: true,
                    };
                    return { data: newData, method: "put", liveSaveUrlKey: "update_user_notification_channel_status" };
                },
            };
        });
        return [
            {
                view: "form",
                widgets,
            },
        ];
    }, [notificationChannelsData]);

    const emailChannelsDynData = useMemo(() => {
        if (!notificationChannelsData || !Array.isArray(notificationChannelsData)) {
            return [];
        }
        const filteredChannels = notificationChannelsData.filter(channel => EMAIL_CHANNELS.includes(channel.code));
        const widgets = filteredChannels.map(channel => {
            return {
                disabled: !canEditNotificationChannels,
                title: channel.name,
                key: "email",
                savedValue: channel.channels,
                widgetType: "contact",
                type: "email",
                async handleContactChange(payload, onSubmitFinish) {
                    const { name, options } = payload;
                    const { action, fieldValue } = options;

                    // handling add or edit cases
                    if (action === "save") {
                        const method = fieldValue.id === 0 ? "post" : "put";
                        const urlKey =
                            fieldValue.id === 0 ? "add_user_notification_channel" : "edit_user_notification_channel";
                        const data = {
                            data: {
                                id: channel.code,
                                value: fieldValue.value,
                            },
                            jsonType: true,
                        };
                        if (fieldValue.id !== 0) {
                            data.pathParams = { id: fieldValue.id };
                        }
                        try {
                            await baseService[method](urlKey, data);
                            onSubmitFinish(true);
                        } catch (e) {
                            console.error("NotificationChannels::handleContactChange: Add or edit error ", e);
                            onSubmitFinish(false);
                        }
                    }

                    if (action === "delete") {
                        const data = {
                            pathParams: { id: fieldValue.id },
                            jsonType: true,
                        };
                        try {
                            await baseService.delete("delete_user_notification_channel", data);
                            onSubmitFinish(true);
                        } catch (e) {
                            console.error("NotificationChannels::handleContactChange: Delete error ", e);
                            onSubmitFinish(false);
                        }
                    }
                    const needRefreshChannels = action === "delete" || fieldValue.id === 0;
                    // we need to request new channels if channel was added or deleted
                    if (needRefreshChannels) {
                        requestNotificationChannelsData();
                    }
                },
            };
        });
        return [
            {
                view: "form",
                widgets,
            },
        ];
    }, [notificationChannelsData]);

    const qrCodeChannels = useMemo(() => {
        if (!notificationChannelsData || !Array.isArray(notificationChannelsData)) {
            return [];
        }
        return notificationChannelsData.filter(channel => QR_CODE_CHANNELS.includes(channel.code));
    }, [notificationChannelsData]);

    function normalizeEmailChannels(data) {
        return data.map(item => {
            if (item.code === 'EMAIL_CHANNEL' && Array.isArray(item.channels) && item.channels.length === 0) {
                return {
                    ...item,
                    channels: [{id: 0, value: ''}]
                };
            }
            return item;
        });
    }

    const requestNotificationChannelsData = async () => {
        setIsLoading(true);

        try {
            await baseService.get("get_user_notification_channels").then(({ success, result }) => {
                setIsLoading(false);
                if (success) {
                    if (result) {
                        const normalizedResult = normalizeEmailChannels(result);
                        setNotificationChannelsData(normalizedResult);
                    }
                } else {
                    console.log("Request user modal notification channels failed.");
                }
            });
        } catch (e) {
            console.error("NotificationChannels::requestNotificationChannelsData: Error ", e);
        } finally {
            setNotificationChannelsTS(new Date().getTime());
        }
    };

    useEffect(() => {
        requestNotificationChannelsData();
    }, []);

    return (
        <div className="user-notification-channels">
            {isLoading && <Loader />}

            {notificationChannelsData && (
                <>
                    {/* Activation channels */}
                    <DynamicForm
                        className="notification-channel-form"
                        formName={"ACTIVATION_CHANNELS"}
                        dynData={activationChannelsDynData}
                        currentId={null}
                        liveSaveIdKey="id"
                        liveSaveUrlKey={""}
                        uploadFileSource="form"
                        liveSave
                        liveSaveJSON
                        onLiveSaveStart={() => {}}
                        onLiveSaveFinish={() => {}}
                        onLiveSaveSuccess={() => {}}
                        refreshDynParams={() => {}}
                        isLiveSaveOnlyValid
                        portalPlacement="right"
                        t={t}
                    />
                    {/* Email channels */}
                    <DynamicForm
                        className="notification-channel-form"
                        formName={"EMAIL_CHANNELS"}
                        dynData={emailChannelsDynData}
                        currentId={null}
                        liveSaveIdKey="id"
                        liveSaveUrlKey={""}
                        uploadFileSource="form"
                        liveSave
                        liveSaveJSON
                        onLiveSaveStart={() => {}}
                        onLiveSaveFinish={() => {}}
                        onLiveSaveSuccess={() => {}}
                        refreshDynParams={() => {}}
                        isLiveSaveOnlyValid
                        portalPlacement="right"
                        t={t}
                        key={notificationChannelsTS}
                    />
                    {/* QR Code channels */}
                    <QRCodeChannels
                        isLoading={isLoading}
                        channels={qrCodeChannels}
                        onQrCodeClose={requestNotificationChannelsData}
                        onChannelRemove={requestNotificationChannelsData}
                        canEditNotificationChannels={canEditNotificationChannels}
                    />
                </>
            )}
        </div>
    );
};

export default NotificationChannels;
